import { useCallback } from 'react';
import get from 'lodash/get';
import { CONFIG } from '@frameio/config';
import type { MeQuery } from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { useAccountVersion } from '../../contexts/ActiveAccountIdContext/useAccountVersion';
import { useActiveAccountId } from '../../contexts/ActiveEntitiesContext';
import {
  PRIMARY_INDUSTRY_GROUP,
  SECONDARY_INDUSTRY_GROUP,
  WEBMAIL_DOMAINS,
} from '../constants';
import { useTrack, useTrackUnsafe } from './useTrack';

async function getClearbitData() {
  const response = await fetch(
    `https://reveal.clearbit.com/v1/companies/reveal?authorization=${CONFIG.clearbitUrl}`,
    {
      method: 'GET',
    }
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

async function isUserQualifiedLead() {
  let companyData;

  try {
    companyData = await getClearbitData();
  } catch (error) {
    return false;
  }

  const employeeCount = get(companyData, 'company.metrics.employees', 0);
  const industry = get(companyData, 'company.category.subIndustry');

  return (
    employeeCount > 1000 ||
    (PRIMARY_INDUSTRY_GROUP.includes(industry) && employeeCount > 50) ||
    (SECONDARY_INDUSTRY_GROUP.includes(industry) && employeeCount > 250)
  );
}

const REGEXP_EMAIL_DOMAIN = /(?<=@)[^.]+(?=\.)/;

export const isWebmail = (email: string | null | undefined): boolean => {
  if (!email) return false;

  const matches = email.match(REGEXP_EMAIL_DOMAIN);
  if (!matches || matches.length === 0) {
    return false;
  }

  const domain = matches[0].toLowerCase();
  return WEBMAIL_DOMAINS.includes(domain);
};

export const useTrackUserFirstLogin = (me: MeQuery['me']) => {
  const userId = me?.id;
  const userEmail = me?.email;
  const accountId = useActiveAccountId();
  const accountVersion = useAccountVersion({ accountId });
  const noV4Account = typeof accountVersion === 'number' && accountVersion < 4;
  const track = useTrack();
  const trackUnsafe = useTrackUnsafe();

  const trackFirstLogin = useCallback(async () => {
    if (!userId || noV4Account) {
      return;
    }

    const localeContext = intl();
    trackUnsafe('first-app-page-viewed', {
      language: localeContext.locale,
    });

    const isQualifiedLead = await isUserQualifiedLead();
    const hasWebMail = isWebmail(userEmail);

    if (!hasWebMail) {
      track('mgl-first-app-page-viewed');

      if (isQualifiedLead) {
        track('mql-first-app-page-viewed');
      }
    }
  }, [userId, userEmail, noV4Account, track, trackUnsafe]);

  return trackFirstLogin;
};
